/**
 * Public Welcome Component
 * 
 * Initial welcome screen with quick action buttons.
 */

import React from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import type { QuickAction } from '../public-chat.types';

interface PublicWelcomeProps {
  quickActions: QuickAction[];
  onQuickAction: (action: QuickAction) => void;
}

export const PublicWelcome: React.FC<PublicWelcomeProps> = ({
  quickActions,
  onQuickAction,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box className={classes.welcome}>
      {/* Hero Icon */}
      <Box className={classes.iconContainer}>
        <RocketLaunchIcon className={classes.icon} />
      </Box>

      {/* Hero Text */}
      <Typography variant="h3" className={classes.title}>
        {t('publicChat.welcomeTitle', 'Welcome to SAIA')}
      </Typography>
      <Typography variant="body1" className={classes.subtitle}>
        {t('publicChat.welcomeSubtitle', 'I\'m your confidential AI agent for LinkedIn automation. Ask me anything about how SAIA can help you reach more people, faster — with maximum discretion.')}
      </Typography>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <Box className={classes.quickActions}>
          {quickActions.map((action) => (
            <Button
              key={action.id}
              variant="outlined"
              onClick={() => onQuickAction(action)}
              className={classes.quickActionButton}
              size={isMobile ? 'small' : 'medium'}
            >
              {action.emoji && <span className={classes.emoji}>{action.emoji}</span>}
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Trust indicators */}
      <Box className={classes.trustIndicators}>
        <Typography variant="caption" className={classes.trustText}>
          ✓ 50,000+ users since 2019
        </Typography>
        <Typography variant="caption" className={classes.trustText}>
          ✓ 14-day free trial
        </Typography>
        <Typography variant="caption" className={classes.trustText}>
          ✓ Cancel anytime
        </Typography>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  welcome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
    maxWidth: 600,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}33 0%, ${theme.palette.secondary.main}33 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    animation: 'float 3s ease-in-out infinite',
    '@keyframes float': {
      '0%, 100%': { transform: 'translateY(0)' },
      '50%': { transform: 'translateY(-10px)' },
    },
  },
  icon: {
    fontSize: 40,
    color: theme.palette.primary.main,
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
  },
  quickActionButton: {
    borderColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
    textTransform: 'none',
    borderRadius: 20,
    padding: theme.spacing(1, 2.5),
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}0A`,
      transform: 'translateY(-2px)',
    },
  },
  emoji: {
    marginRight: theme.spacing(0.75),
    fontSize: '1.1em',
  },
  trustIndicators: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
  },
  trustText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
}));

export default PublicWelcome;

